import apiService from './api';

export interface FingerprintData {
  fingerprintId: number | null;
  fingerprintEnrolledAt: string | null;
  hasFingerprint: boolean;
  fingerprintTemplate: string | null;
}

export interface EnrollFingerprintRequest {
  deviceId: number;
  fingerprintTemplate?: string;
  fingerprintId?: number;
}

export interface VerifyFingerprintRequest {
  deviceId: number;
  fingerprintTemplate: string;
}

export interface DeleteFingerprintRequest {
  deviceId: number;
}

export interface FingerprintDevice {
  id: number;
  deviceId: number;
  name: string;
  status: 'active' | 'inactive';
  tenantId: number | null;
  piWebUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VerifyFingerprintResponse {
  message: string;
  employee: {
    id: number;
    userId: number;
    name: string;
    email: string;
    fingerprintId: number;
  };
}

export interface SaveTemplateRequest {
  fingerprintTemplate: string;
  fingerprintId?: number;
}

const fingerprintApi = {
  /**
   * Enroll fingerprint for employee
   */
  enrollFingerprint: async (employeeId: number, data: EnrollFingerprintRequest) => {
    const response = await apiService.post(`/fingerprint/enroll/${employeeId}`, data);
    return response;
  },

  /**
   * Verify fingerprint
   */
  verifyFingerprint: async (data: VerifyFingerprintRequest): Promise<VerifyFingerprintResponse> => {
    const response = await apiService.post('/fingerprint/verify', data);
    return response;
  },

  /**
   * Get employee fingerprint
   */
  getEmployeeFingerprint: async (employeeId: number): Promise<{ employee: FingerprintData }> => {
    const response = await apiService.get(`/fingerprint/${employeeId}`);
    return response;
  },

  /**
   * Delete fingerprint
   */
  deleteFingerprint: async (employeeId: number, data: DeleteFingerprintRequest) => {
    const response = await apiService.delete(`/fingerprint/${employeeId}`, { data });
    return response;
  },

  /**
   * Check if fingerprint module is enabled
   */
  checkFingerprintEnabled: async (): Promise<{ enabled: boolean; module?: any }> => {
    const response = await apiService.get('/fingerprint/enabled');
    return response;
  },

  /**
   * Save fingerprint template for employee (stateless enrollment)
   */
  saveFingerprintTemplate: async (employeeId: number, data: SaveTemplateRequest) => {
    const response = await apiService.post(`/fingerprint/enroll/${employeeId}`, {
      fingerprintTemplate: data.fingerprintTemplate,
      fingerprintId: data.fingerprintId || 0
    });
    return response;
  },

  /**
   * Get fingerprint template for employee
   */
  getFingerprintTemplate: async (employeeId: number): Promise<{ 
    employee: { 
      id: number;
      name: string;
      email: string;
      fingerprintId: number | null;
      fingerprintEnrolledAt: string | null;
      hasFingerprint: boolean;
      fingerprintTemplate: string | null;
    } 
  }> => {
    const response = await apiService.get(`/fingerprint/${employeeId}`);
    return response;
  },

  /**
   * Get all fingerprint devices
   */
  getFingerprintDevices: async (): Promise<{ devices: FingerprintDevice[] }> => {
    const response = await apiService.get('/fingerprint-devices');
    return response;
  },

  /**
   * Get devices for current admin's tenant
   */
  getMyFingerprintDevices: async (): Promise<{ devices: FingerprintDevice[] }> => {
    const response = await apiService.get('/fingerprint-devices/my-devices');
    return response;
  },

  /**
   * Get single fingerprint device
   */
  getFingerprintDevice: async (id: number): Promise<{ device: FingerprintDevice }> => {
    const response = await apiService.get(`/fingerprint-devices/${id}`);
    return response;
  },

  /**
   * Create fingerprint device (SuperAdmin only)
   */
  createFingerprintDevice: async (data: { deviceId: number; name: string; tenantId?: number; status?: 'active' | 'inactive' }): Promise<{ device: FingerprintDevice }> => {
    const response = await apiService.post('/fingerprint-devices', data);
    return response;
  },

  /**
   * Update fingerprint device (SuperAdmin only)
   */
  updateFingerprintDevice: async (id: number, data: { name?: string; tenantId?: number; status?: 'active' | 'inactive'; deviceId?: number }): Promise<{ device: FingerprintDevice }> => {
    const response = await apiService.put(`/fingerprint-devices/${id}`, data);
    return response;
  },

  /**
   * Delete fingerprint device (SuperAdmin only)
   */
  deleteFingerprintDevice: async (id: number): Promise<void> => {
    await apiService.delete(`/fingerprint-devices/${id}`);
  }
};

export default fingerprintApi;

